/**
 * ConfirmDestructive - Sprint 1, F1.5
 * ====================================
 *
 * Single reusable confirmation dialog for every destructive action.
 *
 * Usage (simple):
 *   <ConfirmDestructive
 *     name="Bank Account"
 *     onConfirm={handleDelete}
 *     trigger={<Button size="icon"><Trash2 /></Button>}
 *   />
 *
 * Usage (high-stakes - require the user to type the object name):
 *   <ConfirmDestructive
 *     name="UBA Current Account"
 *     impact="Deletes 247 transactions tied to this account."
 *     requireType
 *     destructiveLabel="Delete bank account"
 *     onConfirm={handleDelete}
 *     trigger={...}
 *   />
 */
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle } from 'lucide-react';

const ConfirmDestructive = ({
  name,
  impact,
  destructiveLabel,
  confirmingLabel = 'Working…',
  cancelLabel = 'Cancel',
  requireType = false,
  trigger,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  onConfirm,
  // Sprint 6 F6.5 - Archive-instead-of-delete pattern.
  // When `dependentCount > 0` or `onArchive` is provided, surface an
  // "Archive instead" alternative so the user can preserve history.
  dependentCount = 0,
  dependentLabel,
  onArchive,
  archiveLabel = 'Archive instead',
  testIdPrefix = 'confirm-destructive',
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = isControlled ? setControlledOpen : setUncontrolledOpen;

  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);

  const canConfirm = !requireType || typedName.trim() === (name || '').trim();
  const label = destructiveLabel || `Delete ${name || 'this item'}`;

  const handleConfirm = async () => {
    if (!canConfirm || busy) return;
    try {
      setBusy(true);
      await onConfirm?.();
      setOpen(false);
      setTypedName('');
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (busy || typeof onArchive !== 'function') return;
    try {
      setBusy(true);
      await onArchive();
      setOpen(false);
      setTypedName('');
    } finally {
      setBusy(false);
    }
  };

  const showArchiveCta = typeof onArchive === 'function' && (dependentCount > 0 || dependentLabel);
  const dependencyImpact = dependentCount > 0
    ? `${dependentCount} ${dependentLabel || 'record'}${dependentCount === 1 ? '' : 's'} still reference${dependentCount === 1 ? 's' : ''} ${name || 'this item'}. Archiving keeps the history intact and hides it from default lists.`
    : null;

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setTypedName('');
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      {trigger && (
        <AlertDialogTrigger asChild data-testid={`${testIdPrefix}-trigger`}>
          {trigger}
        </AlertDialogTrigger>
      )}
      <AlertDialogContent data-testid={`${testIdPrefix}-dialog`}>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <AlertDialogTitle data-testid={`${testIdPrefix}-title`}>
              {label}?
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 space-y-2" data-testid={`${testIdPrefix}-description`}>
            <span className="block">
              {impact || `This action cannot be undone. ${name || 'This item'} will be permanently removed.`}
            </span>
            {dependencyImpact && (
              <span className="block text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid={`${testIdPrefix}-dependents`}>
                {dependencyImpact}
              </span>
            )}
            {requireType && (
              <span className="block text-xs text-muted-foreground">
                Type <span className="font-semibold text-foreground">{name}</span> to confirm.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {requireType && (
          <div className="space-y-2 py-2">
            <Label htmlFor={`${testIdPrefix}-typed-name`} className="text-xs">Confirm name</Label>
            <Input
              id={`${testIdPrefix}-typed-name`}
              data-testid={`${testIdPrefix}-typed-name`}
              autoComplete="off"
              autoFocus
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={name}
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid={`${testIdPrefix}-cancel`}
            disabled={busy}
          >
            {cancelLabel}
          </AlertDialogCancel>
          {showArchiveCta && (
            <button
              type="button"
              data-testid={`${testIdPrefix}-archive`}
              onClick={handleArchive}
              disabled={busy}
              className="inline-flex items-center justify-center rounded-md px-4 h-10 text-sm font-medium bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors disabled:opacity-60"
            >
              {archiveLabel}
            </button>
          )}
          <AlertDialogAction
            data-testid={`${testIdPrefix}-confirm`}
            onClick={handleConfirm}
            disabled={!canConfirm || busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {busy ? confirmingLabel : label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDestructive;
export { ConfirmDestructive };
